import { memo } from 'react'
import { Handle, Position } from 'reactflow'
import { GraduationCap } from 'lucide-react'
import { STATUS_COLORS, DOK_BY_DIFFICULTY } from './knowledgeMapUtils'

const DIFFICULTIES = ['E', 'M', 'H']

/**
 * Consolidated skill node: ONE node per skill (vs. MasteryNode's one per skill×difficulty).
 * Encodes two dimensions at a glance:
 *  - overall mastery status → card tint/border (same STATUS_COLORS language as the full graph)
 *  - the learner's DOK position → a 3-segment strip; each segment tinted by that DOK's own
 *    mastery, with the CURRENT DOK segment outlined and labeled ("you are here").
 */
function SkillMasteryNodeComponent({ data, selected }) {
  const mastery = data.mastery || { status: 'UNVISITED', totalAttempts: 0, score: 0 }
  const colors = STATUS_COLORS[mastery.status] || STATUS_COLORS.UNVISITED
  const currentDifficulty = data.currentDok?.difficulty
  const allMastered = !!data.currentDok?.allMastered
  const hasAttempts = mastery.totalAttempts > 0
  const score = mastery.score || 0

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-2 !h-2" />
      <div
        className={`w-[240px] rounded-lg px-3 py-2 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
          selected ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-900' : ''
        }`}
        role="button"
        tabIndex={0}
        aria-label={`${data.skill} — ${mastery.status.toLowerCase().replaceAll('_', ' ')}, currently at ${
          data.currentDok?.label || 'DOK 1'
        }`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.currentTarget.click()
          }
        }}
        style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}` }}
      >
        {/* Header: domain + tutor-practice marker + status dot */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 truncate">
            {data.domain}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {data.hasTutorPractice && (
              <GraduationCap
                className="w-3.5 h-3.5 text-indigo-600"
                aria-label="Has tutor practice"
              />
            )}
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors.dot }}
              title={colors.text}
            />
          </span>
        </div>

        {/* Skill name + overall proficiency */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {data.skill}
          </div>
          {hasAttempts && (
            <span className="text-xs font-semibold flex-shrink-0" style={{ color: colors.border }}>
              {(score * 100).toFixed(0)}%
            </span>
          )}
        </div>

        {/* DOK segment strip: per-DOK status tint; current DOK outlined + labeled */}
        <div className="flex gap-1" data-testid="dok-strip">
          {DIFFICULTIES.map((diff) => {
            const entry = data.dokBreakdown?.[diff]
            const segStatus = entry?.mastery?.status || 'UNVISITED'
            const segColors = STATUS_COLORS[segStatus] || STATUS_COLORS.UNVISITED
            const isCurrent = diff === currentDifficulty
            const missing = !entry
            const dokLabel = DOK_BY_DIFFICULTY[diff]
            return (
              <div
                key={diff}
                data-testid={`dok-seg-${diff}`}
                data-current={isCurrent ? 'true' : undefined}
                title={
                  missing
                    ? `${dokLabel} — not available for this skill`
                    : `${dokLabel} — ${segColors.text}${
                        entry.mastery?.totalAttempts
                          ? `, ${Math.round((entry.mastery.score || 0) * 100)}%`
                          : ''
                      }${isCurrent && !allMastered ? ' (you are here)' : ''}`
                }
                className="flex-1 rounded px-1 py-0.5 text-center leading-none"
                style={{
                  backgroundColor: missing ? 'rgba(156,163,175,0.08)' : segColors.bg,
                  border: isCurrent
                    ? `2px solid ${segColors.border}`
                    : '1px solid rgba(156,163,175,0.35)',
                  opacity: missing ? 0.5 : 1
                }}
              >
                <span
                  className={`text-[9px] ${isCurrent ? 'font-bold' : 'font-medium'}`}
                  style={{ color: missing ? '#9CA3AF' : segColors.border }}
                >
                  {isCurrent && !allMastered ? `▶ ${dokLabel}` : dokLabel}
                </span>
              </div>
            )
          })}
        </div>

        {/* Position caption */}
        <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
          {allMastered
            ? 'All DOK levels mastered'
            : `Currently at ${data.currentDok?.label || 'DOK 1'}`}
        </div>
      </div>
    </>
  )
}

export const SkillMasteryNode = memo(SkillMasteryNodeComponent)
